/**
 * OneSignal Web SDK (browser) implementation via react-onesignal.
 */

import type { SubscriptionChangeEvent } from 'react-onesignal';
import {
  deactivatePushSubscriptionOnBackend,
  registerPushSubscriptionWithBackend,
} from '@/services/oneSignal/backendRegistration';
import { storageManager } from '@/storage/storageManager';
import { STORAGE_KEYS } from '@/storage/storageKeys';
import {
  clearOneSignalSessionState,
  getOneSignalAppId,
  lastRegisteredBackendSubscriptionId,
  parseSyncParams,
  runSerializedSync,
  setLastSyncedEmail,
  setLastSyncedPhone,
  setSyncedExternalId,
  type SyncOneSignalUserParams,
} from '@/services/oneSignal/shared';

type WebOneSignal = (typeof import('react-onesignal'))['default'];

/** Tracks whether OneSignal Web SDK init has completed. */
let initialized = false;

/** Shared in-flight init promise — prevents duplicate init under React StrictMode. */
let initPromise: Promise<void> | null = null;

/** Ensures the push subscription change listener is registered only once. */
let subscriptionListenerAttached = false;

let webOneSignalModule: WebOneSignal | null = null;

async function getWebOneSignal(): Promise<WebOneSignal> {
  if (!webOneSignalModule) {
    const mod = await import('react-onesignal');
    webOneSignalModule = mod.default;
  }
  return webOneSignalModule;
}

export function isOneSignalWebAvailable(): boolean {
  return typeof window !== 'undefined' && Boolean(getOneSignalAppId());
}

function logSubscriptionState(oneSignal: WebOneSignal, context = 'current'): void {
  try {
    const subscription = oneSignal.User.PushSubscription;
    const token = subscription.token;
    console.log('[OneSignal] User subscription state:', {
      platform: 'web',
      context,
      optedIn: subscription.optedIn,
      subscriptionId: subscription.id,
      token: token ? `${token.slice(0, 8)}…` : null,
    });
  } catch (error) {
    console.error('[OneSignal] Failed to read subscription state:', error);
  }
}

function attachSubscriptionChangeListener(oneSignal: WebOneSignal): void {
  if (subscriptionListenerAttached) return;

  oneSignal.User.PushSubscription.addEventListener(
    'change',
    (event: SubscriptionChangeEvent) => {
      console.log('[OneSignal] User subscription state changed:', {
        platform: 'web',
        previous: event.previous,
        current: event.current,
      });

      const nextId = event.current?.id ?? null;
      if (nextId) {
        void registerPushSubscriptionWithBackend(nextId, 'web');
      }
    },
  );

  subscriptionListenerAttached = true;
}

export async function initializeOneSignalWeb(): Promise<void> {
  if (typeof window === 'undefined') return;

  const appId = getOneSignalAppId();
  if (!appId) {
    console.error(
      '[OneSignal] VITE_ONESIGNAL_APP_ID is not configured. Push notifications are disabled.',
    );
    return;
  }

  if (initialized) return;

  if (initPromise) {
    await initPromise;
    return;
  }

  initPromise = (async () => {
    try {
      const OneSignal = await getWebOneSignal();

      if (import.meta.env.DEV) {
        OneSignal.Debug.setLogLevel('verbose');
      }

      await OneSignal.init({
        appId,
        serviceWorkerPath: '/OneSignalSDKWorker.js',
        allowLocalhostAsSecureOrigin: true,
        notifyButton: { enable: false },
      });

      initialized = true;
      console.log('[OneSignal] initialized (web)');

      attachSubscriptionChangeListener(OneSignal);
      logSubscriptionState(OneSignal, 'after-init');
    } catch (error) {
      initPromise = null;
      initialized = false;
      throw error;
    }
  })();

  await initPromise;
}

export async function requestNotificationPermissionWeb(): Promise<boolean> {
  await initializeOneSignalWeb();

  if (!isOneSignalWebAvailable()) {
    return false;
  }

  try {
    const OneSignal = await getWebOneSignal();

    if (!OneSignal.Notifications.isPushSupported()) {
      console.info('[OneSignal] Web push is not supported in this browser.');
      return false;
    }

    const alreadyPrompted = await storageManager.get<boolean>(
      STORAGE_KEYS.ONESIGNAL_PERMISSION_PROMPTED,
    );

    if (alreadyPrompted) {
      const hasPermission = OneSignal.Notifications.permission;
      console.log('[OneSignal] Permission already requested previously:', {
        platform: 'web',
        hasPermission,
      });
      logSubscriptionState(OneSignal, 'after-permission-check');
      return hasPermission;
    }

    const granted = await OneSignal.Notifications.requestPermission();

    await storageManager.set(STORAGE_KEYS.ONESIGNAL_PERMISSION_PROMPTED, true);

    if (granted) {
      console.log('[OneSignal] Permission granted');
    } else {
      console.log('[OneSignal] Permission denied');
    }

    logSubscriptionState(OneSignal, 'after-permission-request');

    return granted;
  } catch (error) {
    console.error('[OneSignal] Permission request failed:', error);
    return false;
  }
}

export async function syncOneSignalUserWeb(params: SyncOneSignalUserParams): Promise<void> {
  const parsed = parseSyncParams(params);
  if (!parsed) return;

  await runSerializedSync(async () => {
    await initializeOneSignalWeb();
    if (!isOneSignalWebAvailable()) return;

    const OneSignal = await getWebOneSignal();
    const { artistId, email, phone, needsLogin, needsEmail, needsPhone } = parsed;

    try {
      if (needsLogin) {
        await OneSignal.login(artistId);
        setSyncedExternalId(artistId);
      }

      if (needsEmail && email) {
        OneSignal.User.addEmail(email);
        setLastSyncedEmail(email);
      }

      if (needsPhone && phone) {
        OneSignal.User.addSms(phone);
        setLastSyncedPhone(phone);
      }

      console.log('[OneSignal] User synced', {
        platform: 'web',
        artistId,
        hasEmail: Boolean(email),
        hasPhone: Boolean(phone),
      });

      logSubscriptionState(OneSignal, 'after-login');

      await registerPushSubscriptionWithBackend(OneSignal.User.PushSubscription.id, 'web');
    } catch (error) {
      console.warn('[OneSignal] User sync failed:', error);
    }
  });
}

export async function logOutOneSignalWeb(): Promise<void> {
  try {
    let subscriptionId = lastRegisteredBackendSubscriptionId;

    if (!subscriptionId && isOneSignalWebAvailable() && initialized) {
      const OneSignal = await getWebOneSignal();
      subscriptionId = OneSignal.User.PushSubscription.id ?? null;
    }

    await deactivatePushSubscriptionOnBackend(subscriptionId);

    if (isOneSignalWebAvailable() && initialized) {
      const OneSignal = await getWebOneSignal();
      await OneSignal.logout();
      console.log('[OneSignal] User logged out (web)');
    }
  } catch (error) {
    console.warn('[OneSignal] logOut failed:', error);
  } finally {
    clearOneSignalSessionState();
  }
}
